<template>
  <VaLayout
    :top="{ fixed: true, order: 2 }"
    :left="{ fixed: true, absolute: breakpoints.mdDown, order: 1, overlay: breakpoints.mdDown && !isSidebarMinimized }"
    @leftOverlayClick="isSidebarMinimized = true"
  >
    <template #top>
      <AppNavbar :is-mobile="isMobile" />
    </template>

    <template #left>
      <AppSidebar :minimized="isSidebarMinimized" :animated="!isMobile" :mobile="isMobile" />
    </template>

    <template #content>
      <div :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
        <div v-if="isFullScreenSidebar" class="flex justify-end">
          <VaButton class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
        </div>
      </div>
      <AppLayoutNavigation v-if="!isMobile" class="p-4" />
      <main class="p-4 pt-0">
        <article>
          <RouterView />
        </article>
      </main>
    </template>
  </VaLayout>

  <VaModal v-model="announcementVisible" size="large" hide-default-actions>
    <template #header>
      <div class="flex flex-col gap-2">
        <VaBadge text="公告" color="primary" />
        <h2 class="text-xl font-semibold leading-7">{{ currentAnnouncement?.title }}</h2>
        <div class="text-sm text-secondary">
          发布人：{{ currentAnnouncement?.publisher || '平台' }} · 发布时间：{{
            currentAnnouncement?.createTime || '--'
          }}
        </div>
      </div>
    </template>
    <div class="announcement-content" v-html="safeAnnouncementContent"></div>
    <template #footer>
      <div class="flex justify-end gap-2">
        <VaButton preset="secondary" @click="openAnnouncementPage">查看全部</VaButton>
        <VaButton @click="confirmAnnouncement">我知道了</VaButton>
      </div>
    </template>
  </VaModal>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteUpdate, useRouter } from 'vue-router'
import { useBreakpoint } from 'vuestic-ui'

import { useGlobalStore } from '../stores/global-store'
import { useAuthStore } from '../stores/auth'
import { listNotices, noticeDetail } from '../services/api/portalNotice'

import AppLayoutNavigation from '../components/app-layout-navigation/AppLayoutNavigation.vue'
import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'

const GlobalStore = useGlobalStore()
const authStore = useAuthStore()
const router = useRouter()

const breakpoints = useBreakpoint()

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref(undefined)
const announcementQueue = ref([])
const currentAnnouncement = ref(undefined)
const announcementVisible = ref(false)
const visibleAnnouncementId = ref('')
const closingAnnouncementInternally = ref(false)

const isMobile = ref(false)
const isTablet = ref(false)
const { isSidebarMinimized } = storeToRefs(GlobalStore)

const onResize = () => {
  isSidebarMinimized.value = breakpoints.mdDown
  isMobile.value = breakpoints.smDown
  isTablet.value = breakpoints.mdDown
  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()
  loadAnnouncements()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onBeforeRouteUpdate(() => {
  if (breakpoints.mdDown) {
    // Collapse sidebar after route change for Mobile
    isSidebarMinimized.value = true
  }
})

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

const onCloseSidebarButtonClick = () => {
  isSidebarMinimized.value = true
}

const announcementCacheKey = (id) => `proxy-portal-announcement-read:${authStore.user?.id || 'guest'}:${id}`

const loadAnnouncements = async () => {
  try {
    const result = await listNotices({ page: 1, size: 10, type: 1 })
    announcementQueue.value = result.records.filter((item) => !localStorage.getItem(announcementCacheKey(item.id)))
    showNextAnnouncement()
  } catch {
    announcementQueue.value = []
  }
}

const showNextAnnouncement = async () => {
  const next = announcementQueue.value.shift()
  if (!next) {
    currentAnnouncement.value = undefined
    visibleAnnouncementId.value = ''
    announcementVisible.value = false
    return
  }
  try {
    currentAnnouncement.value = next.content ? next : await noticeDetail(next.id)
    visibleAnnouncementId.value = currentAnnouncement.value?.id || next.id
    announcementVisible.value = true
  } catch {
    localStorage.setItem(announcementCacheKey(next.id), '1')
    await showNextAnnouncement()
  }
}

const closeAnnouncementInternally = async () => {
  closingAnnouncementInternally.value = true
  announcementVisible.value = false
  await nextTick()
  closingAnnouncementInternally.value = false
}

const confirmAnnouncement = async () => {
  const id = visibleAnnouncementId.value || currentAnnouncement.value?.id
  if (id) {
    localStorage.setItem(announcementCacheKey(id), '1')
  }
  await closeAnnouncementInternally()
  await showNextAnnouncement()
}

const openAnnouncementPage = async () => {
  const id = visibleAnnouncementId.value || currentAnnouncement.value?.id
  if (id) {
    localStorage.setItem(announcementCacheKey(id), '1')
    router.push({ name: 'notices', query: { noticeId: id } })
  } else {
    router.push({ name: 'notices' })
  }
  await closeAnnouncementInternally()
}

const safeAnnouncementContent = computed(() => sanitizeHtml(currentAnnouncement.value?.content || ''))

watch(announcementVisible, (visible, oldVisible) => {
  if (!visible && oldVisible && !closingAnnouncementInternally.value && visibleAnnouncementId.value) {
    localStorage.setItem(announcementCacheKey(visibleAnnouncementId.value), '1')
  }
})

const sanitizeHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc.querySelectorAll('script, iframe, object, embed, link, style').forEach((item) => item.remove())
  doc.querySelectorAll('*').forEach((item) => {
    ;[...item.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase()
      const value = attr.value.trim().toLowerCase()
      if (name.startsWith('on') || value.startsWith('javascript:')) {
        item.removeAttribute(attr.name)
      }
    })
  })
  return doc.body.innerHTML
}
</script>

<style lang="scss" scoped>
// Prevent icon jump on animation
.va-sidebar {
  width: unset !important;
  min-width: unset !important;
}

.announcement-content {
  color: var(--va-text-primary);
  line-height: 1.75;
}

.announcement-content :deep(h1),
.announcement-content :deep(h2),
.announcement-content :deep(h3) {
  margin: 0.8em 0 0.45em;
  font-weight: 700;
}

.announcement-content :deep(p) {
  margin: 0.6em 0;
}
</style>
